import { ReactElement } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';

const useStyles = makeStyles(({
  containerPrice: {
    position: 'relative',
    width: '100%',
    height: 35,
  },
  titlePrice: {
    fontSize: 14,
    fontWeight: 500,
    color: '#727993',
    position: 'absolute',
    top: 0,
    bottom: 0,
  },
  valuePrice: {
    fontSize: 14,
    fontWeight: 600,
    color: '#4d5369',
    position: 'absolute',
    top: 0,
    right: 0,
  },
  discountPrice: {
    fontSize: 14,
    fontWeight: 600,
    color: '#008060',
    position: 'absolute',
    top: 0,
    right: 0,
  },
  containerTotalPrice: {
    position: 'relative',
    width: '100%',
    height: 50,
  },
  titleTotalPrice: {
    fontSize: 14,
    fontWeight: 500,
    color: '#727993',
    position: 'absolute',
    top: 1,
    bottom: 0,
  },
  totalPrice: {
    fontSize: 20,
    fontWeight: 700,
    color: '#000000',
    position: 'absolute',
    top: 0,
    right: 0,
    marginTop: 10,
  },
  totalPriceLoading: {
    fontSize: 20,
    fontWeight: 700,
    position: 'absolute',
    top: 0,
    right: 0,
    marginTop: 10,
    color: '#5959EB',
  },
  tooltipIcon: {
    width: '0.75em',
    height: '0.75em',
  },
  newCustomerText1: {
    fontWeight: 500,
    fontSize: 13,
    color: '#6A6A6A',
  },
  newCustomerText2: {
    fontSize: 12,
    fontWeight: 600,
    color: '#414141',
    marginLeft: 5,
    backgroundColor: '#E8E5FF',
    paddingTop: 1,
    paddingRight: 10,
    paddingBottom: 1,
    paddingLeft: 10,
    borderRadius: 4,
  },
}));

interface PricingSectionProps {
  subTotal: number;
  totalDiscount: number;
  totalTax: number;
  totalPrice: number;
  showOptInFirstThreeLicenses: boolean;
  showTax: boolean;
  loading: boolean;
}

const PricingSection = (props: PricingSectionProps) => {
  const classes = useStyles();
  const {
    subTotal,
    totalDiscount,
    totalTax,
    totalPrice,
    showOptInFirstThreeLicenses,
    showTax,
    loading,
  } = props;
  const intl = useIntl();

  const renderViewPrice = (textId: string, value: ReactElement, tooltipId: string = '') => (
    <div className={classes.containerPrice}>
      <p className={classes.titlePrice}>
        <FormattedMessage id={textId} />
        {tooltipId && (
          <Tooltip
            title={<FormattedMessage id={tooltipId} />}
            placement="top"
            arrow
          >
            <IconButton>
              <InfoIcon className={classes.tooltipIcon} />
            </IconButton>

          </Tooltip>
        )}
      </p>
      {
        loading
          ? <CircularProgress className={classes.totalPriceLoading} size={20} />
          : value
      }
    </div>
  );

  const renderPromotionNote = () => (
    <Box
      id="expand-plan-new-customer-note"
      display="flex"
      justifyContent="space-between"
      paddingBottom={1}
    >
      <Box id="expand-plan-new-customer-note-label-1">
        <span className={classes.newCustomerText1}>
          <FormattedMessage id="page.expand.plan.new.customers.description_1" />
        </span>
      </Box>
      <Box id="expand-plan-new-customer-note-label-2">
        <span className={classes.newCustomerText2}>
          <FormattedMessage id="page.expand.plan.new.customers.description_2" />
        </span>
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        key="pricing-section"
        id="pricing-section"
        width="100%"
      >
        {renderViewPrice(
          'checkout.subtotal',
          (
            <p className={classes.valuePrice}>
              $
              {subTotal.toLocaleString('en-us')}
            </p>
          ),
        )}

        {totalDiscount > 0 && renderViewPrice(
          'checkout.discount',
          (
            <p className={classes.discountPrice}>
              - $
              {intl.formatNumber(totalDiscount || 0)}
            </p>
          ),
        )}

        {showTax && renderViewPrice(
          'subscriptions.testops_platform.tax',
          (
            <p className={classes.valuePrice}>
              $
              {intl.formatNumber(totalTax || 0)}
            </p>
          ),
        )}

        <div className={classes.containerTotalPrice}>
          <p className={classes.titleTotalPrice}>
            <FormattedMessage id="license_utilization.total" />
          </p>
          {
            loading
              ? <CircularProgress className={classes.totalPriceLoading} size={20} />
              : (
                <p className={classes.totalPrice}>
                  $
                  {intl.formatNumber(totalPrice || 0)}
                </p>
              )
          }
        </div>

        {/* Promotion note */}
        {showOptInFirstThreeLicenses && renderPromotionNote()}

      </Box>
    </>
  );
};

export default PricingSection;
